import { useTranslation } from 'react-i18next';
import { DashboardMode, IDashboard, IDashboardYard } from '.';
import InfoCard from '../../../components/organisms/dashboard/info-card';
import classNames from 'classnames';
import Card from '../../../components/atoms/card';
import Text from '../../../components/atoms/text';
import Label from '../../../components/atoms/text/label';
import SVGIcon from '../../../components/atoms/icons';
import ProgressBar from '../../../components/organisms/dashboard/progress-bar';
import {
	BarChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	Bar,
	ResponsiveContainer,
	LineChart,
	Line,
} from 'recharts';
import CircularProgressBar from '../../../components/organisms/dashboard/circular-progress-bar';
import { numberToStr } from '../../../utils/number';
import Modal from '../../../components/organisms/modal';
import { useState } from 'react';

interface DashboardDetailedProps {
	data: IDashboard;
	mode: DashboardMode;
	carregando: boolean;
}

const DashboardDetailed = ({ data, mode, carregando }: DashboardDetailedProps) => {
	const { t } = useTranslation();
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [modalData, setModalData] = useState<IDashboardYard | null>(null);
	const dadosConsumo = data.consumptionData.slice(-6).map((registro) => ({
		...registro,
		frozen: Number(registro.frozen),
		cold: Number(registro.cold),
	}));

	const onModalOpen = (index: number) => {
		setIsModalVisible(true);
		setModalData(data.yards[index]);
	};

	const handleClose = () => {
		setIsModalVisible(false);
		setModalData(null);
	};

	const calcRemainingSockets = () =>
		Number(modalData?.sockets || 0);

	return (
		<div
			className={classNames('flex flex-col gap-y-3', {
				hidden: mode === 'simple',
			})}
		>
			<div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
				<InfoCard
					carregando={carregando}
					className="border-blue"
					title={t('dashboard.monitored_total')}
					value={data.containersTotalMonitored}
					iconClassName="fill-blue h-8"
					icon="Container"
				/>
				<InfoCard
					carregando={carregando}
					className="border-green"
					title={t('dashboard.efficiency')}
					value={data.efficiency}
					iconClassName="fill-transparent h-8"
					icon="Bars"
				/>
				<InfoCard
					carregando={carregando}
					className="border-yellow"
					title={t('dashboard.available_sockets')}
					value={data.sockets}
					icon="PowerSocket"
					iconClassName="h-8"
				/>
				<InfoCard
					carregando={carregando}
					className="border-red"
					title={t('misc.alerts')}
					value={data.alerts}
					iconClassName="h-8 stroke-red"
					icon="Error"
				/>
				<InfoCard
					carregando={carregando}
					className="border-primary"
					title={t('dashboard.frozen')}
					value={data.frozen}
					iconClassName="fill-primary h-8"
					icon="Frozen"
				/>
				<InfoCard
					carregando={carregando}
					className="border-primary-light"
					title={t('dashboard.cold')}
					value={data.cold}
					iconClassName="fill-primary-light h-8"
					icon="Cold"
				/>
			</div>
			<div className="grid md:grid-cols-[1fr_2fr] gap-3">
				<Card>
					<div className="grid lg:grid-cols-2 p-2 gap-2">
						<Text className="col-span-full font-semibold">
							{t('dashboard.yard_distribution')}
						</Text>
						{data.yards.map((item, index) => (
							<div
								key={`${item.name}-${index}`}
								className="grid grid-cols-[1fr_auto] border-default rounded-lg p-2 gap-1 items-center cursor-pointer"
								onClick={() => onModalOpen(index)}
							>
								<Text className="font-semibold text-sm">
									{item.name}
								</Text>
								<Label className="flex gap-1 items-center text-xs">
									<SVGIcon
										className="h-3 w-auto stroke-red"
										icon="Error"
									/>
									{item.alerts} {t('misc.alerts')}
								</Label>
								<ProgressBar
									value={item.monitoredCount}
									total={item.total}
								/>
								<Text className="text-xs">
									{t('dashboard.quantity')}:{' '}
									{item.monitoredCount}/{item.total}
								</Text>
							</div>
						))}
					</div>
				</Card>
				<Card className="flex flex-col p-2 gap-4">
					<Text className="col-span-full font-semibold">
						{t('dashboard.energy_consumption')}
					</Text>
					<ResponsiveContainer width="100%" height={400}>
						<LineChart data={dadosConsumo}>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="#f0f0f0"
							/>
							<XAxis
								dataKey="label"
								tick={{ fill: '#6b7280' }}
								tickLine={{ stroke: '#6b7280' }}
							/>
							<YAxis
								domain={[0, 'dataMax']}
								tick={{ fill: '#6b7280' }}
								tickLine={{ stroke: '#6b7280' }}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: 'white',
									border: 'none',
									borderRadius: '8px',
									boxShadow:
										'0 4px 6px -1px rgba(0, 0, 0, 0.1)',
								}}
								formatter={(value: number) => [`${value} kWh`, '']}
							/>
							<Legend />
							<Line
								type="monotone"
								dataKey="frozen"
								name={t('dashboard.frozen', 'Frozen')}
								stroke="#8884d8"
								strokeWidth={2}
								dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
								activeDot={{ r: 6 }}
							/>
							<Line
								type="monotone"
								dataKey="cold"
								name={t('dashboard.cold', 'Cold')}
								stroke="#82ca9d"
								strokeWidth={2}
								dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
								activeDot={{ r: 6 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				</Card>
			</div>
			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
				{/* Containers Status */}
				<Card className="flex flex-col p-2 gap-2 row-span-2">
					<Text className="col-span-full font-semibold">
						{t('dashboard.containers_status')}
					</Text>
					<div className="place-self-center">
						<CircularProgressBar
							values={[
								data.status.operational,
								data.status.alert,
								data.status.critical,
							]}
							total={data.containersTotalMonitored}
							colors={['#4caf50', '#e53935', '#ffc107']}
						/>
					</div>
					<div className="flex justify-around">
						<Label className="flex items-center gap-1">
							<div className="size-4 bg-red rounded-md"></div>
							{t('dashboard.alert')}: {data.status.alert}
						</Label>
						<Label className="flex items-center gap-1">
							<div className="size-4 bg-yellow rounded-md"></div>
							{t('dashboard.critical')}: {data.status.critical}
						</Label>
					</div>
					<Label className="flex items-center gap-1 place-self-center">
						<div className="size-4 bg-green rounded-md"></div>
						{t('dashboard.operational')}: {data.status.operational}
					</Label>
				</Card>

				{/* Harbor Current Fee */}
				<Card className="flex flex-col p-2 gap-1">
					<Text className="font-semibold">
						{t('dashboard.value')}
					</Text>
					<div className="flex items-center justify-between w-full">
						<Text className="font-semibold text-lg">
							{data.value}
						</Text>
						<SVGIcon className="h-4 w-auto" icon="Financial" />
					</div>
					<Text className="text-xs font-medium">
						{t('dashboard.harbor_current_fee')}
					</Text>
				</Card>

				{/* Total Consumption */}
				<Card className="flex flex-col p-2 gap-1">
					<Text className="font-semibold">
						{t('dashboard.consumption_total')}
					</Text>
					<div className="flex items-center justify-between w-full">
						<Text className="font-semibold text-lg">
							{data.monthlyCost.value}
						</Text>
						<SVGIcon className="h-4 w-auto" icon="Rate" />
					</div>
				</Card>

				{/* Monthly Cost */}
				<Card className="flex flex-col p-2 gap-1">
					<Text className="font-semibold">
						{t('dashboard.monthly_cost')}
					</Text>
					<div className="flex items-center justify-between w-full">
						<Text className="font-semibold text-lg">
							{data.consumption}
						</Text>
						<SVGIcon className="h-4 w-auto" icon="Financial" />
					</div>
					<Text className="text-xs font-medium flex gap-1 items-center">
						<SVGIcon
							className={classNames('h-3 w-auto', {
								'stroke-green': data.monthlyCost.rate > 0,
								'stroke-red rotate-180':
									data.monthlyCost.rate < 0,
							})}
							icon="Arrow2"
						/>
						{/* <b
							className={classNames('text-xs', {
								'text-green': data.monthlyCost.rate > 0,
								'text-red': data.monthlyCost.rate < 0,
							})}
						>
							{numberToStr(data.monthlyCost.rate)}%
						</b>
						{t('dashboard.compared_previous_month')} */}
					</Text>
				</Card>

				{/* Scheduled Maintenances */}
				<Card className="col-span-2 sm:col-span-3 grid sm:grid-cols-3 lg:grid-cols-5 gap-2 p-2">
					<Text className="font-semibold col-span-full">
						{t('dashboard.scheduled_maintenances')}
					</Text>
					{data.maintenances.length == 0 && (
						<Text className="italic">
							{t('dashboard.no_maintenances')}
						</Text>
					)}
					{data.maintenances.map((maintenance) => (
						<div className="border-default rounded-md flex p-2 gap-2 items-center">
							<div className="size-6 rounded-md bg-yellow flex items-center justify-center">
								<SVGIcon
									className="h-4 w-auto stroke-black"
									icon="Error"
								/>
							</div>
							<div className="flex flex-col">
								<Text className="text-sm font-semibold">
									{maintenance.name}
								</Text>
								<Text className="text-xs font-semibold text-gray-label">
									{maintenance.occurrencies}{' '}
									{t('dashboard.occurrencies_today')}
								</Text>
							</div>
						</div>
					))}
				</Card>
			</div>
			<Modal
				isModalVisible={isModalVisible}
				onClose={handleClose}
				className="flex flex-col gap-4"
			>
				<Text as="h3">{modalData?.name}</Text>
				<Text>
					<b>{t('monitor.containers_monitored')}: </b>
					{modalData?.monitoredCount}/{modalData?.total}
				</Text>
				<div className="flex">
					<Text className="flex-1">
						<b>{t('dashboard.cold')}: </b>
						{modalData?.cold}
					</Text>
					<Text className="flex-1">
						<b>{t('dashboard.frozen')}: </b>
						{modalData?.frozen}
					</Text>
				</div>
				<Text>
					<b>{t('misc.alerts')}: </b>
					{modalData?.alerts}
				</Text>
				<Text>
					<b>{t('dashboard.available_sockets')}: </b>
					{calcRemainingSockets()}
				</Text>
			</Modal>
		</div>
	);
};

export default DashboardDetailed;
